import html2canvas from "html2canvas";
import qz from "qz-tray";
import { toast } from "react-toastify";


// QZ Tray Certificate Configuration
qz.security.setCertificatePromise(function(resolve, reject) {
  resolve();
});

qz.security.setSignatureAlgorithm("SHA512");
qz.security.setSignaturePromise(function(toSign) {
  return function(resolve, reject) {
    resolve();
  };
});

/**
 * Generate QR Code as base64 data URL
 */
const generateQRCodeDataURL = async (data) => {
  try {
    // const qrData = `Ticket:${data.ticket_number || 'N/A'}|Plate:${data.vehicle_license_plate || 'N/A'}|Time:${data.parked_time || ''}`;
    const qrData = data.ticket_number;
    const size = 150;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(qrData)}&format=png`;
    
    // Fetch and convert to base64
    const response = await fetch(qrUrl);
    const blob = await response.blob();
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("QR Code generation error:", error);
    return null;
  }
};

/**
 * QZ Tray Print Handler for Tickets and Receipts
 * @param {Object} data - Ticket or Receipt data
 * @param {string} type - "ticket" or "receipt"
 * @param {string} logoUrl - Logo image URL
 * @param {Object} templateData - Template configuration (layout, company name, footer)
 */
export const handlePrintTicket = async (data, type, logoUrl, templateData) => {
  try {
    toast.info(`Preparing ${type} for printing...`);

    // Generate QR code for tickets
    let qrCodeDataURL = null;
    if (type === "ticket") {
      qrCodeDataURL = await generateQRCodeDataURL(data);
    }

    // Connect to QZ Tray if not already connected
    if (!qz.websocket.isActive()) {
      await qz.websocket.connect();
      console.log("Connected to QZ Tray");
    }

    // Find printer
    const printers = await qz.printers.find();
    console.log("Available printers:", printers);
    
    let printer;
    if (printers && printers.length > 0) {
      printer = printers.find(p => p.toLowerCase().includes("bixolon")) || printers[0];
      console.log("Selected printer:", printer);
    } else {
      throw new Error("No printers found");
    }

    // Generate print content based on type
    const printContent = type === "ticket" 
      ? generateTicketContent(data, logoUrl, templateData, qrCodeDataURL)
      : generateReceiptContent(data, logoUrl, templateData);

    // QZ Tray configuration for thermal printer
    const config = qz.configs.create(printer, {
      scaleContent: false,
      rasterize: false,
      colorType: "blackwhite",
      density: 0,
      margins: { top: 0, right: 0, bottom: 0, left: 0 },
      size: { width: 3, height: null },
      units: "in"
    });

    // Print data with pixel/html format
    const printData = [{
      type: "pixel",
      format: "html",
      flavor: "plain",
      data: printContent
    }];

    await qz.print(config, printData);
    toast.success(`${type === "ticket" ? "Ticket" : "Receipt"} printed successfully!`);

  } catch (error) {
    console.error("Print error:", error);
    
    if (error.message.includes("Unable to establish connection")) {
      toast.error("Please install and run QZ Tray application");
    } else if (error.message.includes("No printers")) {
      toast.error("No printer found. Please connect a printer.");
    } else if (error.message.includes("Cannot find printer")) {
      toast.error("Printer not accessible. Please check printer connection.");
    } else {
      toast.error(`Failed to print ${type}: ${error.message}`);
    }
  }
};


export const handleShareTicket = async (data, type, logoUrl, templateData) => {
  try {
    let qrCodeDataURL = null;
    if (type === "ticket") {
      qrCodeDataURL = await generateQRCodeDataURL(data);
    }

    const htmlContent = type === "ticket"
      ? generateTicketContent(data, logoUrl, templateData, qrCodeDataURL)
      : generateReceiptContent(data, logoUrl, templateData);

    // STEP 1: Ek hidden iframe banao (sabse safe method)
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.left = "-9999px";
    iframe.style.top = "-9999px";
    iframe.style.width = "80mm";
    iframe.style.height = "1000px"; // zyada se zyada
    iframe.style.border = "none";
    iframe.style.opacity = "0";
    iframe.style.pointerEvents = "none";
    iframe.style.zIndex = "-1000";

    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(htmlContent);
    iframeDoc.close();

    // Wait for images + fonts to load inside iframe
    await new Promise(resolve => {
      iframe.onload = () => setTimeout(resolve, 400); // extra safe
      // agar onload na chale to bhi timeout se resolve ho jaye
      setTimeout(resolve, 800);
    });

    // Ab html2canvas iframe ke body pe lagao
    const canvas = await html2canvas(iframeDoc.body, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      width: iframeDoc.body.scrollWidth,
      height: iframeDoc.body.scrollHeight,
    });

    // Cleanup iframe
    document.body.removeChild(iframe);

    const dataUrl = canvas.toDataURL("image/png");

    // Convert to file
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], `${type}.png`, { type: "image/png" });

    // Share or Download
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        title: type === "ticket" ? "Parking Ticket" : "Receipt",
        text: "Your PARK EASE document",
        files: [file],
      });
      toast.success("Shared successfully!");
    } else {
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${type}.png`;
      a.click();
      toast.info("Image downloaded");
    }

  } catch (error) {
    console.error("Share failed:", error);
    toast.error("Share failed. Try again.");
  }
};
/**
 * Generate HTML content for Ticket
 */
const generateTicketContent = (data, logoUrl, templateData, qrCodeDataURL) => {
  const header = generateHeader(logoUrl, templateData, "ticket", qrCodeDataURL);
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <style>
        * { 
          margin: 0; 
          padding: 0; 
          box-sizing: border-box; 
        }
        
        @page {
          size: 80mm auto;
          margin: 0;
        }
        
        body { 
          font-family: 'Arial', 'Helvetica', sans-serif; 
          font-size: 10px;
           font-weight: 400; 
          width: 80mm;
          max-width: 80mm;
          padding: 4mm 8mm;
          color: #000000 !important;
          background: #ffffff;
          line-height: 1.3;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        .center { 
          text-align: center; 
        }
        
        .bold { 
          font-weight: 700; 
        }
        
        .header { 
          margin-bottom: 4px;
          padding-bottom: 4px;
          // border-bottom: 2px solid #000000;
          position: relative;
        }
        
        .header-with-qr {
          display: table;
          width: 100%;
        }
        
        .header-left {
          display: table-cell;
          vertical-align: top;
          width: 70%;
        }
        
        .header-qr {
          display: table-cell;
          vertical-align: top;
          width: 30%;
          text-align: right;
        }
        
        .header-qr img {
          width: 55px !important;
          height: 55px !important;
          display: inline-block;
          padding: 2px;
          background: #ffffff;
        }
        
        .header-left img { 
          max-height: 45px;
          max-width: 100%;
          width: auto;
          height: auto;
          margin: 0 0 6px 0;
          display: block;
          object-fit: contain;
        }
        
        .header-left h3 { 
          font-size: 13px;
          // font-weight: 700;
          margin: 4px 0;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #000000;
        }
        
        .section { 
          border-bottom: 1px dashed #000000; 
          padding: 8px 0;
          page-break-inside: avoid;
        }
        
        .section:last-of-type {
          border-bottom: none;
        }
        
        .row { 
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin: 4px 0;
          gap: 4px;
        }
        
        .label { 
          // font-size: 11px;
          // font-weight: 900;
          color: #000000 !important;
        }
        
        .value { 
          // font-weight: 400;
          // font-size: 11px;
          color: #4a4a4a !important;
        }
        
        .section-title { 
          // font-weight: 900;
          // font-size: 12px;
        }
        
        .footer { 
          // border-top: 2px solid #000000;
          padding-top: 4px;
          margin-top: 4px;
          text-align: center;
        }
        
        .footer-text { 
          // font-weight: 600; 
          // font-size: 10px;
          margin-bottom: 4px;
          color: #000000;
        }
        
        
        .powered-by {
          font-size: 9px;
          color: #000000;
          margin-top: 4px;
        }
      </style>
    </head>
    <body>
      ${header}
      
      <div class="section">
        ${data.ticket_number ? `
        <div class="row">
          <span class="label">Ticket Number:</span>
          <span class="value">${data.ticket_number}</span>
        </div>
        ` : ""}
        ${data.parking_ticket ? `
        <div class="row">
          <span class="label">Parking Ticket:</span>
          <span class="value">${data.parking_ticket}</span>
        </div>
        ` : ""}
        ${data.customer_name ? `
        <div class="row">
          <span class="label">Customer Name:</span>
          <span class="value">${data.customer_name}</span>
        </div>
        ` : ""}
        ${data.customer_phone ? `
        <div class="row">
          <span class="label">Customer Phone:</span>
          <span class="value">${data.customer_phone}</span>
        </div>
        ` : ""}
        ${data.parked_time ? `
        <div class="row">
          <span class="label">Entry Time:</span>
          <span class="value">${new Date(data.parked_time).toLocaleString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true
          })}</span>
        </div>
        ` : ""}
        ${data.entry_gate && data.entry_gate.trim() !== "" ? `
        <div class="row">
          <span class="label">Entry Gate:</span>
          <span class="value">${data.entry_gate}</span>
        </div>
        ` : ""}
      </div>
      
      <div class="section">
        <div class="section-title">Vehicle Details</div>
        ${data.vehicle_license_plate ? `
        <div class="row">
          <span class="label">Plate Number:</span>
          <span class="value">${data.vehicle_license_plate}</span>
        </div>
        ` : ""}
        ${data.vehicle_type ? `
        <div class="row">
          <span class="label">Vehicle Type:</span>
          <span class="value">${data.vehicle_type}</span>
        </div>
        ` : ""}
        ${data.vehicle_make ? `
        <div class="row">
          <span class="label">Vehicle Make:</span>
          <span class="value">${data.vehicle_make}</span>
        </div>
        ` : ""}
        ${data.vehicle_model ? `
        <div class="row">
          <span class="label">Vehicle Model:</span>
          <span class="value">${data.vehicle_model}</span>
        </div>
        ` : ""}
        ${data.vehicle_color ? `
        <div class="row">
          <span class="label">Vehicle Color:</span>
          <span class="value">${data.vehicle_color}</span>
        </div>
        ` : ""}
      </div>
      
      ${data.parking_fee ? `
      <div class="section">
        <div class="section-title">Parking Fee</div>
        <div class="row">
          <span class="label">Amount:</span>
          <span class="value">${data.parking_fee}</span>
        </div>
      </div>
      ` : ""}
      
      <div class="footer">
        ${templateData.ticket_footer_text ? `
          <div class="footer-text">${templateData.ticket_footer_text}</div>
        ` : ""}
        <div class="powered-by">Powered by <span class="bold">PARK EASE</span></div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Generate HTML content for Receipt
 */
const generateReceiptContent = (data, logoUrl, templateData) => {
  const header = generateHeader(logoUrl, templateData, "receipt", null);
  const { receipt_number, receipt_date, ticket_number, vehicle, parking, location } = data;

const my_date = receipt_date;
const d = new Date(my_date);

// Date only (UTC)
const date = `${String(d.getUTCDate()).padStart(2,"0")}/${String(d.getUTCMonth()+1).padStart(2,"0")}/${d.getUTCFullYear()}`;

// Time only (UTC, 12-hour)
let hours = d.getUTCHours();
const minutes = String(d.getUTCMinutes()).padStart(2, "0");
const ampm = hours >= 12 ? "PM" : "AM";
hours = hours % 12;
hours = hours ? hours : 12;
const time = `${String(hours).padStart(2,"0")}:${minutes} ${ampm}`;


  let taxBreakdown = [];
  try {
    taxBreakdown = parking?.tax_breakdown
      ? typeof parking.tax_breakdown === "string"
        ? JSON.parse(parking.tax_breakdown)
        : parking.tax_breakdown
      : [];
    if (!Array.isArray(taxBreakdown)) taxBreakdown = [];
  } catch (error) {
    taxBreakdown = [];
  }
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <style>
        * { 
          margin: 0; 
          padding: 0; 
          box-sizing: border-box; 
        }
        
        @page {
          size: 80mm auto;
          margin: 0;
        }
        
        body { 
          font-family: 'Arial Black', 'Arial', sans-serif;
         font-family: 'Arial', 'Helvetica', sans-serif; 
          font-size: 10px;
           font-weight: 400; 
          width: 80mm;
          max-width: 80mm;
          padding: 2mm 8mm;
          color: #000000 !important;
          background: #ffffff;
          line-height: 1.3;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        .center { 
          text-align: center; 
        }
        
        .bold { 
          font-weight: 700; 
        }
        
        .header { 
          margin-bottom: 12px;
          padding-bottom: 8px;
          // border-bottom: 2px solid #000000;
        }
        
        .header img { 
          max-height: 50px;
          max-width: 60mm;
          width: auto;
          height: auto;
          margin: 0 auto 6px auto;
          display: block;
          object-fit: contain;
          filter: grayscale(100%) contrast(200%);
        }
        
        .header h3 { 
          // font-size: 15px;
          // font-weight: 900;
          margin: 6px 0;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #000000;
          word-wrap: break-word;
        }
        
        .section { 
          border-bottom: 1px dashed #000000; 
          padding: 8px 0;
          page-break-inside: avoid;
        }
        
        .section:last-of-type {
          border-bottom: none;
        }
        
        .row { 
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin: 4px 0;
          gap: 4px;
          flex-wrap: wrap;
        }
        
        .label { 
          color: #000000 !important;
          flex: 1 1 45%;
          min-width: 0;
          text-align: left;
          word-wrap: break-word;
          word-break: break-word;
          // font-size: 10px;
          // font-weight: 700;
        }
        
        .value { 
          // font-weight: 600; 
          // font-size: 10px;
          color: #4a4a4a !important;
          flex: 1 1 45%;
          min-width: 0;
          text-align: right;
          word-wrap: break-word;
          word-break: break-word;
        }
        
        .section-title { 
          // font-weight: 700; 
          // font-size: 11px; 
          margin-bottom: 6px;
          text-transform: uppercase;
          color: #000000;
          letter-spacing: 0.3px;
          word-wrap: break-word;
        }
        
        .total-row {
          // border-top: 2px solid #000000;
          padding-top: 6px;
          margin-top: 6px;
          // font-weight: 700;
          // font-size: 12px;
        }
        
        .footer { 
          // border-top: 2px solid #000000;
          padding-top: 10px;
          margin-top: 10px;
          text-align: center;
        }
        
        .footer-text { 
          // font-weight: 600; 
          // font-size: 10px;
          margin-bottom: 6px;
          color: #000000;
          word-wrap: break-word;
        }
        
        .header-info { 
          display: flex; 
          justify-content: space-between; 
          margin-bottom: 6px;
          gap: 4px;
          flex-wrap: wrap;
        }
        
        .header-info > div { 
          flex: 1 1 30%;
          min-width: 0;
          text-align: center;
        }
        
        .header-info .label {
          // font-size: 9px;
          color: #000000;
          display: block;
          margin-bottom: 2px;
          // font-weight: 500;
          word-wrap: break-word;
        }
        
        .header-info .value {
          // font-weight: 700;
          color: #000000;
          font-size: 10px;
          display: block;
          word-wrap: break-word;
          word-break: break-word;
        }
        
        .tax-section {
          margin-top: 6px;
        }
        
        .tax-title {
          // font-weight: 700;
          // font-size: 10px;
          margin-bottom: 4px;
          color: #000000;
          word-wrap: break-word;
        }
        
        .powered-by {
          font-size: 9px;
          color: #000000;
          margin-top: 4px;
        }
      </style>
    </head>
    <body>
      ${header}
      
      <div class="section">
        <div class="header-info">
          ${receipt_number ? `
          <div>
            <span class="label">Receipt #</span>
            <span class="value">${receipt_number}</span>
          </div>
          ` : ""}
          ${receipt_date ? `
          <div>
            <span class="label">Date</span>
            <span class="value">${date}</span>
          </div>
          <div>
            <span class="label">Time</span>
            <span class="value">${time}</span>
          </div>
          ` : ""}
        </div>
      </div>
      
      <div class="section">
        <div class="section-title">Vehicle Details</div>
        ${ticket_number ? `
        <div class="row">
          <span class="label">Ticket #:</span>
          <span class="value">${ticket_number}</span>
        </div>
        ` : ""}
        ${vehicle?.plate ? `
        <div class="row">
          <span class="label">Plate Number:</span>
          <span class="value">${vehicle.plate}</span>
        </div>
        ` : ""}
        ${vehicle?.type ? `
        <div class="row">
          <span class="label">Vehicle Type:</span>
          <span class="value">${vehicle.type}</span>
        </div>
        ` : ""}
        ${vehicle?.make ? `
        <div class="row">
          <span class="label">Vehicle Make:</span>
          <span class="value">${vehicle.make}</span>
        </div>
        ` : ""}
        ${vehicle?.model ? `
        <div class="row">
          <span class="label">Vehicle Model:</span>
          <span class="value">${vehicle.model}</span>
        </div>
        ` : ""}
        ${vehicle?.color ? `
        <div class="row">
          <span class="label">Vehicle Color:</span>
          <span class="value">${vehicle.color}</span>
        </div>
        ` : ""}
      </div>
      
      <div class="section">
        <div class="section-title">Parking Details</div>
        ${parking?.parked_at ? `
        <div class="row">
          <span class="label">Start:</span>
          <span class="value">${new Date(parking.parked_at).toLocaleString("en-US", {
            hour12: true,
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit"
          })}</span>
        </div>
        ` : ""}
        ${parking?.unparked_at ? `
        <div class="row">
          <span class="label">End:</span>
          <span class="value">${new Date(parking.unparked_at).toLocaleString("en-US", {
            hour12: true,
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit"
          })}</span>
        </div>
        ` : ""}
        ${parking?.duration ? `
        <div class="row">
          <span class="label">Duration:</span>
          <span class="value">${parking.duration}</span>
        </div>
        ` : ""}
        ${location?.branch ? `
        <div class="row">
          <span class="label">Branch:</span>
          <span class="value">${location.branch}</span>
        </div>
        ` : ""}
        ${location?.location ? `
        <div class="row">
          <span class="label">Location:</span>
          <span class="value">${location.location}</span>
        </div>
        ` : ""}
        ${location?.entry_gate ? `
        <div class="row">
          <span class="label">Entry Gate:</span>
          <span class="value">${location.entry_gate}</span>
        </div>
        ` : ""}
        ${location?.slot ? `
        <div class="row">
          <span class="label">Slot:</span>
          <span class="value">${location.slot}</span>
        </div>
        ` : ""}
      </div>
      
      <div class="section">
        <div class="section-title">Payment Breakdown</div>
        ${parking?.payment_amount ? `
        <div class="row">
          <span class="label">Valet Amount:</span>
          <span class="value">${parking.payment_amount}</span>
        </div>
        ` : ""}
        ${parking?.parking_fee_collected ? `
        <div class="row">
          <span class="label">Parking Fee:</span>
          <span class="value">${parking.parking_fee_collected}</span>
        </div>
        ` : ""}
        ${taxBreakdown.length > 0 ? `
          <div class="tax-section">
            <div class="tax-title">Tax Breakdown:</div>
            ${taxBreakdown.map(tax => `
              <div class="row">
                <span class="label">${tax.type || "Tax"} (${tax.percent || "0"}):</span>
                <span class="value">${tax.amount || "0.00"}</span>
              </div>
            `).join("")}
          </div>
        ` : ""}
         </div>
        ${parking?.total_amount ? `
        <div class="row total-row">
          <span>TOTAL:</span>
          <span>${parking.total_amount}</span>
        </div>
        ` : ""}
     
      
      <div class="footer">
        ${templateData.receipt_footer_text ? `
          <div class="footer-text">${templateData.receipt_footer_text}</div>
        ` : ""}
        <div class="powered-by">Powered by <span class="bold">PARK EASE</span></div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Generate header based on layout configuration
 */
const generateHeader = (logoUrl, templateData, type, qrCodeDataURL = null) => {
  const layoutValue = type === "ticket" 
    ? templateData.ticket_layout 
    : templateData.receipt_layout;
  
  const companyName = type === "ticket"
    ? templateData.ticket_company_name || "PARK EASE VALET"
    : templateData.receipt_company_name || "PARK EASE VALET";

  const getLayout = (value) => {
    if (value.includes("img")) return "img";
    if (value.includes("name-top")) return "name";
    if (value.includes("side")) return "side";
    return "img";
  };

  const getContent = (value) => {
    if (value.includes("both")) return "both";
    if (value.includes("logo")) return "logo";
    if (value.includes("name")) return "name";
    return "both";
  };

  const layout = getLayout(layoutValue);
  const content = getContent(layoutValue);
  
  const showLogo = logoUrl && content !== "name";
  const showName = companyName && content !== "logo";
  const showQR = qrCodeDataURL && type === "ticket";

  if (!showLogo && !showName) return "";

  // Image on top layout
  if (layout === "img") {
    return `
      <div class="header">
        <div class="header-with-qr">
          <div class="header-left">
            ${showLogo ? `<img src="${logoUrl}" alt="Logo" />` : ""}
            ${showName ? `<h3>${companyName}</h3>` : ""}
          </div>
          ${showQR ? `
            <div class="header-qr">
              <img src="${qrCodeDataURL}" alt="QR" />
            </div>
          ` : ""}
        </div>
      </div>
    `;
  }

  // Name on top layout
  if (layout === "name") {
    return `
      <div class="header">
        <div class="header-with-qr">
          <div class="header-left">
            ${showName ? `<h3>${companyName}</h3>` : ""}
            ${showLogo ? `<img src="${logoUrl}" alt="Logo" />` : ""}
          </div>
          ${showQR ? `
            <div class="header-qr">
              <img src="${qrCodeDataURL}" alt="QR" />
            </div>
          ` : ""}
        </div>
      </div>
    `;
  }

  // Side by side layout
  if (layout === "side") {
    return `
      <div class="header">
        <div class="header-with-qr">
          <div class="header-left" style="display: flex; align-items: center; gap: 8px;">
            ${showLogo ? `<img src="${logoUrl}" alt="Logo" style="height: 40px; max-width: 45%;" />` : ""}
            ${showName ? `<h3 style="margin: 0; font-size: 12px; color: #000000;">${companyName}</h3>` : ""}
          </div>
          ${showQR ? `
            <div class="header-qr">
              <img src="${qrCodeDataURL}" alt="QR" />
            </div>
          ` : ""}
        </div>
      </div>
    `;
  }

  return "";
};